import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Trader, ApiResponse } from '../types';
import clsx from 'clsx';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
  suspended: 'bg-yellow-100 text-yellow-800',
  fraud: 'bg-red-100 text-red-800',
};

export default function Traders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';
  const accountType = searchParams.get('accountType') || '';

  const { data, isLoading } = useQuery({
    queryKey: ['traders', { status, accountType, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (accountType) params.set('accountType', accountType);
      params.set('page', page.toString());

      const response = await api.get<ApiResponse<Trader[]>>(`/traders?${params}`);
      return response.data;
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Traders</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Forex platform traders referred by affiliates.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex gap-4">
        <select
          value={status}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) {
              params.set('status', e.target.value);
            } else {
              params.delete('status');
            }
            params.set('page', '1');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
          <option value="fraud">Fraud</option>
        </select>
        <select
          value={accountType}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) {
              params.set('accountType', e.target.value);
            } else {
              params.delete('accountType');
            }
            params.set('page', '1');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Account Types</option>
          <option value="demo">Demo</option>
          <option value="live">Live</option>
          <option value="islamic">Islamic</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                Trader ID
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Affiliate
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Account
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Deposits
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Total Lots
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Registered
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-[var(--text-muted)]">
                  Loading...
                </td>
              </tr>
            ) : data?.data?.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-[var(--text-muted)]">
                  No traders found
                </td>
              </tr>
            ) : (
              data?.data?.map((trader) => (
                <tr key={trader.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                    <div className="font-mono text-[var(--text-primary)]">{trader.externalId}</div>
                    <div className="text-[var(--text-muted)] text-xs">{trader.country || 'Unknown'}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {trader.affiliate?.name || '-'}
                    {trader.affiliate?.referralCode && (
                      <div className="text-xs">
                        <code>{trader.affiliate.referralCode}</code>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={clsx(
                        'badge',
                        statusColors[trader.status as keyof typeof statusColors]
                      )}
                    >
                      {trader.status}
                    </span>
                    {trader.isVerified && (
                      <span className="ml-1 badge badge-success">Verified</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)] capitalize">
                    {trader.accountType}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                    ${trader.totalDeposits.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                    {trader.totalLots.toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {new Date(trader.registeredAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page - 1).toString());
                setSearchParams(params);
              }}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page + 1).toString());
                setSearchParams(params);
              }}
              disabled={page >= data.pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
